import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { guideSections, filterGuideSections } from "./guidesContent";
import { YouTubeEmbed } from "./YouTubeEmbed";
import { FaqItem } from "./FaqItem";

/**
 * /guides — Help & Guides page. Instructional videos and FAQs per feature
 * pillar. Sections are deep-linkable via hash anchors (e.g. /guides#watchlists)
 * so checklist tasks and empty states can point at the relevant guide.
 * Content lives entirely in guidesContent.ts.
 */
function Guides() {
  const [query, setQuery] = useState("");
  const location = useLocation();
  const scrolledRef = useRef(false);

  const sections = useMemo(
    () => filterGuideSections(guideSections, query),
    [query]
  );

  // Scroll to the hash-anchored section once on mount / hash change.
  useEffect(() => {
    const id = location.hash.replace("#", "");
    if (!id) return;
    // Defer until sections have rendered.
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      scrolledRef.current = true;
    }, 100);
    return () => clearTimeout(t);
  }, [location.hash]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          Help &amp; Guides
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Short videos and answers to get the most out of Newsquawk.
        </p>
        {/* Search */}
        <div className="relative mt-4 max-w-md">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search guides and FAQs…"
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 py-2 pl-9 pr-3 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-squawk-blue focus:border-transparent"
          />
        </div>
      </div>

      {sections.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No guides match &ldquo;{query}&rdquo;. Try a different search, or ask
          us directly via the chat bubble.
        </p>
      )}

      {/* Sections */}
      <div className="space-y-12">
        {sections.map((section) => (
          <section
            key={section.id}
            id={section.id}
            className="scroll-mt-16"
            aria-labelledby={`${section.id}-heading`}
          >
            <h2
              id={`${section.id}-heading`}
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              {section.title}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {section.tagline}
            </p>

            {/* Videos (placeholder slot when none exist yet) */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {section.videos.length > 0 ? (
                section.videos.map((video) => (
                  <YouTubeEmbed
                    key={video.youtubeId || video.title}
                    youtubeId={video.youtubeId}
                    title={video.title}
                  />
                ))
              ) : (
                <YouTubeEmbed youtubeId="" title={section.title} />
              )}
            </div>

            {/* FAQs */}
            {section.faqs.length > 0 && (
              <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4">
                {section.faqs.map((faq) => (
                  <FaqItem key={faq.q} faq={faq} />
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

export default Guides;
